import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Card, Form, Modal, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';

const errorText = error => typeof error.response?.data === 'string' ? error.response.data : 'The request failed. Please try again.';
const date = value => value ? new Date(value * 1000).toLocaleString() : 'Never';

export default function CustomerManagement() {
  const [customers, setCustomers] = useState([]);
  const [identities, setIdentities] = useState([]);
  const [activity, setActivity] = useState([]);
  const [selected, setSelected] = useState('yds');
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [secret, setSecret] = useState(null);
  const [usage, setUsage] = useState(null);
  const [newUser, setNewUser] = useState({ username: '' });
  const [newCustomer, setNewCustomer] = useState({ id: '', name: '', brand: 'previews' });
  const [limits, setLimits] = useState({ site_limit: 25, storage_mb: 2048, artifact_mb: 100 });

  const load = useCallback(async () => {
    const [c, i, a] = await Promise.all([
      axios.get('/api/management/customers/'), axios.get('/api/management/identities/'), axios.get('/api/management/activity/')
    ]);
    setCustomers(c.data); setIdentities(i.data); setActivity(a.data);
  }, []);
  useEffect(() => { load().catch(error => setMessage({ variant: 'danger', text: errorText(error) })); }, [load]);
  const customer = customers.find(item => item.id === selected);
  useEffect(() => { setUsage(null); }, [selected]);
  useEffect(() => {
    if (customer) setLimits({ site_limit: customer.site_limit, storage_mb: customer.storage_limit / 1048576,
      artifact_mb: customer.artifact_limit / 1048576 });
  }, [customer]);

  const act = async (fn, success) => {
    setBusy(true); setMessage(null);
    try { await fn(); await load(); if (success) setMessage({ variant: 'success', text: success }); }
    catch (error) { setMessage({ variant: 'danger', text: errorText(error) }); }
    finally { setBusy(false); }
  };

  const toggleCustomer = () => {
    if (customer.enabled && !window.confirm(`Disable publishing access for ${customer.name}? Existing websites will stay online. Existing access credentials will be revoked.`)) return;
    act(() => axios.patch(`/api/management/customers/${selected}/`, { enabled: !customer.enabled }), 'Customer access updated');
  };

  const issue = identity => act(async () => {
    const { data } = await axios.post(`/api/management/identities/${identity.id}/credentials/`, { label: 'MCP publishing' });
    setSecret({ token: data.token, username: identity.username });
  });

  return <>
    <h1>Customers</h1>
    <p>Manage ownership and publishing access. Your administrator account can manage every customer’s sites.</p>
    {message && <Alert variant={message.variant} dismissible onClose={() => setMessage(null)}>{message.text}</Alert>}
    <Form.Group className="mb-3" controlId="managedCustomer">
      <Form.Label>Customer</Form.Label>
      <Form.Select value={selected} onChange={e => setSelected(e.target.value)}>
        {customers.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
      </Form.Select>
    </Form.Group>

    {customer && <Card className="mb-4"><Card.Body>
      <Card.Title>{customer.name}</Card.Title>
      <p>{customer.sites} sites · Publishing {customer.enabled ? 'enabled' : 'disabled'} · Preview domains: {customer.brands.join(', ') || 'Managed internally'}</p>
      <p><Button disabled={busy} size="sm" variant="outline-secondary" onClick={() => act(async () => {
        const { data } = await axios.get(`/api/management/customers/${selected}/usage/`); setUsage(data);
      })}>Calculate retained storage</Button>{usage && <span className="ms-2">{(usage.used_bytes / 1048576).toFixed(1)} MB used of {(usage.limit_bytes / 1048576).toFixed(0)} MB</span>}</p>
      {selected !== 'internal' && <>
        <Button disabled={busy} variant={customer.enabled ? 'outline-danger' : 'success'} onClick={toggleCustomer}>
          {customer.enabled ? 'Disable publishing access' : 'Enable publishing access'}
        </Button>
        <Form className="mt-3" onSubmit={e => { e.preventDefault(); act(() => axios.patch(`/api/management/customers/${selected}/`, {
          site_limit: Number(limits.site_limit), storage_limit: Math.round(Number(limits.storage_mb) * 1048576),
          artifact_limit: Math.round(Number(limits.artifact_mb) * 1048576)
        }), 'Limits updated'); }}>
          <div className="row">
            {[
              ['site_limit', 'Maximum sites'], ['storage_mb', 'Total retained storage (MB)'], ['artifact_mb', 'Maximum built site (MB)']
            ].map(([key, label]) => <Form.Group key={key} className="col-md-4 mb-3" controlId={`limit-${key}`}>
              <Form.Label>{label}</Form.Label><Form.Control type="number" min="1" required value={limits[key]}
                onChange={e => setLimits({ ...limits, [key]: e.target.value })} />
            </Form.Group>)}
          </div>
          <Button disabled={busy} type="submit" variant="outline-primary">Save limits</Button>
        </Form>
      </>}
    </Card.Body></Card>}

    <h2 className="h4">Publishing identities</h2>
    <Table responsive striped>
      <thead><tr><th>User</th><th>Access</th><th>Connections</th><th>Actions</th></tr></thead>
      <tbody>{identities.filter(item => item.customer_id === selected).map(identity => <tr key={identity.id}>
        <td>{identity.username}</td><td>{identity.enabled ? 'Enabled' : 'Disabled'}</td>
        <td>{identity.credentials.filter(item => item.kind !== 'session').map(credential => <div key={credential.id} className="mb-2">
          <strong>{credential.label}</strong> — {credential.revoked_at ? 'Revoked' : credential.expires_at === -1 ? 'No expiry' : `Expires ${date(credential.expires_at)}`}<br />
          <small>Last used: {date(credential.last_used_at)}</small>{!credential.revoked_at && <Button size="sm" variant="link" disabled={busy}
            onClick={() => act(() => axios.delete(`/api/management/credentials/${credential.id}/`), 'Connection revoked')}>Revoke</Button>}
        </div>)}</td>
        <td>{identity.role === 'publisher' && <>
          <Button className="me-2 mb-2" size="sm" disabled={busy || !customer?.enabled || !identity.enabled} onClick={() => issue(identity)}>Create access token</Button>
          <Button className="mb-2" size="sm" variant="outline-secondary" disabled={busy}
            onClick={() => act(() => axios.patch(`/api/management/identities/${identity.id}/`, { enabled: !identity.enabled }), 'Identity updated')}>
            {identity.enabled ? 'Disable user' : 'Enable user'}
          </Button>
        </>}</td>
      </tr>)}</tbody>
    </Table>
    {selected !== 'internal' && <Form className="mb-4" onSubmit={e => { e.preventDefault(); act(async () => {
      await axios.post('/api/management/identities/', { ...newUser, customer_id: selected });
      setNewUser({ username: '' });
    }, 'Publishing identity created'); }}>
      <div className="row">
        <Form.Group className="col-md-5 mb-3" controlId="publisherUsername"><Form.Label>New publishing username</Form.Label>
          <Form.Control required value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })} autoComplete="off" />
        </Form.Group>
      </div>
      <Button type="submit" disabled={busy}>Add publishing identity</Button>
    </Form>}

    <Card className="mb-4"><Card.Body><Card.Title>MCP connection</Card.Title>
      <p>Server address: <code>{window.location.origin}/mcp</code></p>
      <p>Connect with a publishing access token in the client’s secret settings. This release supports clients with bearer headers, or the local Mogged MCP adapter. Browser-based OAuth sign-in is not available yet.</p>
      <p className="mb-0">New access tokens expire after 30 days by default. Each connection shows its expiry status. Revoking access prevents further publishing; it does not remove hosted websites.</p>
    </Card.Body></Card>

    <details className="mb-4"><summary>Add another customer</summary>
      <Form className="mt-3" onSubmit={e => { e.preventDefault(); act(async () => {
        await axios.post('/api/management/customers/', newCustomer); setSelected(newCustomer.id);
        setNewCustomer({ id: '', name: '', brand: 'previews' });
      }, 'Customer created with publishing disabled'); }}>
        {[['id','Identifier'],['name','Customer name']].map(([key,label]) => <Form.Group className="mb-2" controlId={`customer-${key}`} key={key}>
          <Form.Label>{label}</Form.Label><Form.Control required value={newCustomer[key]} onChange={e => setNewCustomer({ ...newCustomer, [key]: e.target.value })} />
        </Form.Group>)}
        <Form.Group className="mb-3" controlId="customer-brand"><Form.Label>Provisioned preview domain</Form.Label>
          <Form.Select value={newCustomer.brand} onChange={e => setNewCustomer({ ...newCustomer, brand: e.target.value })}>
            <option value="previews">internal.previews.co.nz</option><option value="yds">dev.yds.agency</option>
            <option value="mogged_nz">internal.getmogged.co.nz</option><option value="mogged_au">internal.getmogged.com.au</option>
            <option value="buynz_digital">internal.buynzdigital.org.nz</option>
          </Form.Select>
        </Form.Group><Button disabled={busy} type="submit">Create customer</Button>
      </Form>
    </details>

    <h2 className="h4">Recent activity</h2>
    <Table responsive size="sm"><thead><tr><th>When</th><th>Actor</th><th>Action</th><th>Site</th></tr></thead>
      <tbody>{activity.map(event => <tr key={event.id}><td>{date(event.at)}</td><td>{event.actor_id || 'System'}</td>
        <td>{event.action}</td><td>{event.site_id ? <Link to={`/sites/${event.site_id}`}>{event.site_id}</Link> : '—'}</td></tr>)}</tbody>
    </Table>
    <Modal show={!!secret} onHide={() => setSecret(null)}><Modal.Header closeButton><Modal.Title>Access token for {secret?.username}</Modal.Title></Modal.Header>
      <Modal.Body><p>Copy this token now. It will not be shown again.</p><Form.Control as="textarea" readOnly aria-label="New access token" value={secret?.token || ''} rows={3} /></Modal.Body>
      <Modal.Footer><Button onClick={() => setSecret(null)}>Done</Button></Modal.Footer>
    </Modal>
  </>;
}
